import React from "react";

const GOLD = "#D4AF35";

const Cite = ({ authors, journal, year }) => (
  <span className="text-[13px]" style={{ color: "rgba(255,255,255,0.5)" }}>
    ({authors}, <em>{journal}</em>, {year})
  </span>
);

// Self-contained graphic supplied by the user (copy/colors/labels are final).
const GRAPHIC_CSS = `
.sfs-glp{max-width:560px;margin:28px auto;padding:24px 22px 18px;background:#111;border:1px solid #2a2a2a;border-radius:18px;font-family:Inter,-apple-system,"Segoe UI",sans-serif;color:#fafafa;box-sizing:border-box}
.sfs-glp *{box-sizing:border-box}
.sfs-glp__eyebrow{font-weight:700;font-size:12px;letter-spacing:2.4px;text-transform:uppercase;color:#D4AF35;text-align:center;margin:0 0 6px}
.sfs-glp__title{font-family:Archivo,sans-serif;font-weight:900;font-size:24px;line-height:1.15;text-transform:uppercase;text-align:center;margin:0 0 20px;color:#fff}
.sfs-glp__row{display:grid;grid-template-columns:repeat(10,1fr);gap:6px}
.sfs-glp__lb{aspect-ratio:3/4;border-radius:8px;display:flex;align-items:flex-end;justify-content:center;padding-bottom:6px;font-family:Archivo,sans-serif;font-weight:800;font-size:11px}
.sfs-glp__lb--fat{background:#D4AF35;color:#0d0d0d}
.sfs-glp__lb--lean{background:#8E1B1B;color:#ffd9d9;box-shadow:inset 0 0 0 1.5px #c43c3c}
.sfs-glp__brackets{display:grid;grid-template-columns:6fr 4fr;gap:6px;margin-top:10px}
.sfs-glp__br{border-top:2px solid;padding-top:8px;text-align:center}
.sfs-glp__br--fat{border-color:#D4AF35}
.sfs-glp__br--lean{border-color:#c43c3c}
.sfs-glp__num{font-family:Archivo,sans-serif;font-weight:900;font-size:22px;line-height:1}
.sfs-glp__br--fat .sfs-glp__num{color:#D4AF35}
.sfs-glp__br--lean .sfs-glp__num{color:#ff6b6b}
.sfs-glp__lab{font-weight:700;font-size:11px;letter-spacing:1.4px;text-transform:uppercase;color:#cfcfcf;margin-top:4px;line-height:1.3}
.sfs-glp__src{font-size:11px;color:rgba(255,255,255,.45);text-align:center;margin:16px 0 0;line-height:1.4}
@media (max-width:420px){
  .sfs-glp{padding:20px 14px 14px}
  .sfs-glp__title{font-size:20px}
  .sfs-glp__row{gap:4px}
  .sfs-glp__lb{font-size:9px;border-radius:6px;padding-bottom:4px}
  .sfs-glp__num{font-size:18px}
  .sfs-glp__lab{font-size:9.5px;letter-spacing:1px}
}
`;

const Glp1Graphic = () => (
  <div
    className="sfs-glp"
    role="img"
    aria-label="Of every 10 pounds lost on a GLP-1 medication in the STEP 1 trial, about 6 were fat and about 4 were lean mass, which includes muscle."
    data-testid="glp1-graphic"
  >
    <style>{GRAPHIC_CSS}</style>
    <p className="sfs-glp__eyebrow">What the scale doesn't tell you</p>
    <p className="sfs-glp__title">Every 10 pounds lost</p>
    <div className="sfs-glp__row" aria-hidden="true">
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <div key={n} className="sfs-glp__lb sfs-glp__lb--fat">{n}</div>
      ))}
      {[7, 8, 9, 10].map((n) => (
        <div key={n} className="sfs-glp__lb sfs-glp__lb--lean">{n}</div>
      ))}
    </div>
    <div className="sfs-glp__brackets" aria-hidden="true">
      <div className="sfs-glp__br sfs-glp__br--fat">
        <div className="sfs-glp__num">~6 LBS</div>
        <div className="sfs-glp__lab">Fat</div>
      </div>
      <div className="sfs-glp__br sfs-glp__br--lean">
        <div className="sfs-glp__num">~4 LBS</div>
        <div className="sfs-glp__lab">
          Lean mass,
          <br />
          including muscle
        </div>
      </div>
    </div>
    <p className="sfs-glp__src">Body-composition results, STEP 1 trial (Wilding et al., N Engl J Med, 2021)</p>
  </div>
);

export const Glp1Section = ({ H2, body }) => (
  <section className="w-[90%] max-w-[760px] mx-auto mt-20" data-testid="glp1-section">
    <H2>What the before-and-after photos don't show you.</H2>
    <div className={`mt-7 ${body}`}>
      <p>You've seen the commercials. You've probably wondered if a weight-loss shot is the answer.</p>
      <p>
        For some women, it's part of the answer. Our vetted medical partners prescribe GLP weight loss medications when
        they're appropriate.
      </p>
      <p>
        But the scale can't tell fat from muscle. In one of the largest GLP-1 trials, as much as 40% of the weight people
        lost was lean mass, and lean mass is mainly muscle. <Cite authors="Wilding et al." journal="N Engl J Med" year="2021" />
      </p>
    </div>
    <Glp1Graphic />
    <div className={body}>
      <p>
        After 40, that's muscle you can't afford to give away. It holds your metabolism up, protects your joints and keeps
        you strong. Lose it, and you can end up smaller but weaker and softer than when you started. And in the same
        research, most people regained about two-thirds of the weight within a year of stopping.{" "}
        <Cite authors="Wilding et al." journal="Diabetes Obes Metab" year="2022" /> The muscle doesn't come back on its own.
      </p>
    </div>
    <div className="mt-7 border-l-[3px] pl-5 py-1" style={{ borderColor: GOLD }} data-testid="glp1-callout">
      <p className="text-lg md:text-xl font-semibold leading-snug" style={{ color: GOLD }}>
        The shot can take the weight off. It can't choose what comes off. That's our job.
      </p>
    </div>
    <div className={`mt-7 ${body}`}>
      <p>
        If a GLP weight loss medication is right for you, it works with your plan, not instead of it. That means strength
        training and protein to protect your muscle while the weight comes off, and a plan for what happens when the
        medication stops. One coach watching all seven systems, so the scale and the mirror both get better.
      </p>
    </div>
  </section>
);
